import { SwaggerRequestProps } from './constants'

/**
 * The `<location>_<name>` convention, in one place.
 *
 * A Swagger step carries its inputs as a flat map, because that is what the flow
 * builder renders and what the props processor validates. Which part of the HTTP
 * request an input belongs to is therefore encoded in its key. Three packages
 * depend on that encoding: the server writes the keys when it turns an OpenAPI
 * document into piece props, and the engine and the connection service read them
 * back to reassemble the call. Writing and reading live in the same file so the
 * separator and the set of locations cannot be changed on one side only — a
 * mismatch would not fail to compile, it would silently drop parameters from
 * every generated piece.
 */
const SWAGGER_INPUT_KEY_SEPARATOR = '_'

const SWAGGER_INPUT_LOCATIONS: string[] = Object.values(SwaggerRequestProps)

/**
 * Prefix per location, built once at module load. Deriving the table from the
 * enum is what keeps the writer and the reader in step; matching a whole prefix
 * also means a key is never cut apart before we know it is one of ours.
 */
const SWAGGER_INPUT_PREFIXES: { location: SwaggerRequestProps, prefix: string }[] =
    Object.values(SwaggerRequestProps).map((location) => ({
        location,
        prefix: `${location}${SWAGGER_INPUT_KEY_SEPARATOR}`,
    }))

/**
 * `location` is a plain string because it also comes from third-party documents
 * (`parameter.in`, `securityScheme.in`), which may name a location we do not
 * support — `cookie`, for instance. Such a key is built, but `parseSwaggerInputKey`
 * refuses to read it back, so the value never reaches the request. Callers that
 * want to reject early can validate with `isSwaggerInputLocation` first.
 */
export const swaggerInputKey = (location: string, name: string): string =>
    `${location}${SWAGGER_INPUT_KEY_SEPARATOR}${name}`

export const isSwaggerInputLocation = (
    location: string,
): location is SwaggerRequestProps => SWAGGER_INPUT_LOCATIONS.includes(location)

/**
 * Reads a key back into the location and the name the API expects. Returns
 * `undefined` for anything that is not an input of a known location — the `url`
 * markdown prop, the `auth` object, a `cookie_` parameter — so callers drop it.
 *
 * Only the prefix is consumed: a parameter name may itself contain the
 * separator, and `query_start_date` has to stay `start_date`.
 */
export const parseSwaggerInputKey = (
    key: string,
): { location: SwaggerRequestProps, name: string } | undefined => {
    for (const { location, prefix } of SWAGGER_INPUT_PREFIXES) {
        if (key.startsWith(prefix)) {
            return { location, name: key.slice(prefix.length) }
        }
    }
    return undefined
}

/**
 * Renders an input as the string a path segment, a header or a query parameter
 * has to carry. Inputs are typed `unknown` because a Swagger property can
 * declare any JSON type, and `String()` would spell an object as
 * `[object Object]` — a value no API can act on and no administrator can
 * recognise in a run log. An absent value becomes empty rather than the literal
 * `"undefined"`, which would otherwise travel as part of the URL.
 */
export const toSwaggerStringValue = (value: unknown): string => {
    if (value === null || value === undefined) {
        return ''
    }
    if (typeof value === 'string') {
        return value
    }
    if (typeof value === 'number' || typeof value === 'boolean') {
        return value.toString()
    }
    return JSON.stringify(value)
}

export type CategorizedInputs = {
    path: Record<string, string>
    body: Record<string, unknown>
    header: Record<string, string>
    query: Record<string, string>
}

export const emptyCategorizedInputs = (): CategorizedInputs => ({
    path: {},
    body: {},
    header: {},
    query: {},
})

/**
 * Places one value in the part of the request its location names. Only the body
 * keeps the value as-is: a JSON payload carries any type, whereas a path
 * segment, a header and a query parameter travel as text.
 */
export const setCategorizedInput = (params: {
    target: CategorizedInputs
    location: SwaggerRequestProps
    name: string
    value: unknown
}): void => {
    const { target, location, name, value } = params
    if (location === SwaggerRequestProps.BODY) {
        target.body[name] = value
        return
    }
    target[location][name] = toSwaggerStringValue(value)
}

/**
 * Splits a flat input map into the four parts of an HTTP request. Keys that
 * name no known location are dropped rather than guessed at.
 */
export const categorizeInputs = (
    inputs: Record<string, unknown>,
): CategorizedInputs => {
    const target = emptyCategorizedInputs()

    for (const [key, value] of Object.entries(inputs)) {
        const parsed = parseSwaggerInputKey(key)
        if (parsed === undefined) {
            continue
        }
        setCategorizedInput({
            target,
            location: parsed.location,
            name: parsed.name,
            value,
        })
    }

    return target
}

export const mergeCategorizedInputs = (
    target: CategorizedInputs,
    source: CategorizedInputs,
): CategorizedInputs => ({
    path: { ...target.path, ...source.path },
    body: { ...target.body, ...source.body },
    header: { ...target.header, ...source.header },
    query: { ...target.query, ...source.query },
})
